import { useState, useContext, useEffect, useRef, useCallback } from "react";
import {
  useFirestoreOrderBy,
  useFirestoreGeneralOrderBy
} from "../../../firebase/useFirestore.js";
import Location from "../../../hooks/Location.js";
import { client } from "../../../hooks/Client.js";
import useSettings from "../../../hooks/Settings.jsx";
import sanitizeHtml from "../../../utils/sanitizeHtml";
import { Auth } from "../../../StateManagment/Auth.js";
import { auth as firebaseAuth, db, timestamp } from "../../../firebase/config.js";
import { USE_ORCHESTRATOR } from "../../../constants/deccaOrchestrator.js";
import uuid from "react-uuid";
import InputContainer from "./InputContainer.jsx";
import StatusLine from "./StatusLine.jsx";
import DeepLinkChip from "./DeepLinkChip.jsx";
import isNearBottom from "../../../helpers/decca/isNearBottom.js";
import ResumeChip from "./ResumeChip.jsx";
import ConfirmationCard from "./ConfirmationCard.jsx";
import parseSseStream from "../../../utils/parseSseStream";
import Tooltip from "../../common/Tooltip.jsx";
import DeccaIcon from "./DeccaIcon.jsx";
import DeccaAvatar from "./DeccaAvatar.jsx";
import readAloud from "../../open/readAloud.js";
import { DeccaContext } from "../../../StateManagment/DeccaContext.js";
import { DEFAULT_ALLOWED_TAGS } from "../../../utils/sanitizeHtml";
import extractSuggestions from "../../../helpers/decca/extractSuggestions";

// The system prompt asks Decca to format answers with <h2>/<h3> headings; the
// default allowlist strips those, leaving headings as bare unstyled text runs.
// Chat-only override — the shared default stays untouched (ParagraphWidget
// mirrors it).
const DECCA_ALLOWED_TAGS = [...DEFAULT_ALLOWED_TAGS, "h2", "h3"];

/**
 * Strip HTML to plain text so text-to-speech reads words, not markup/entities.
 * @param {string} html
 * @returns {string}
 */
const htmlToText = (html) => {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").trim();
};

/**
 * @param {object} props
 * @param {string} props.conversationId - Het open gesprek (`Conversations.ID`)
 * @param {string} [props.initialMessage] - Eerste vraag uit het lege scherm; wordt
 *   één keer verstuurd zodra het welkomstbericht geladen is
 */
const Decca = ({ conversationId, initialMessage = "" }) => {
  // Context
  const [auth] = useContext(Auth);
  const {
    subject,
    contextData,
    selectedActivity,
    selectedEffect,
    tenantOverride,
    tenantNameOverride,
    entryPoint,
    tocContext,
  } = useContext(DeccaContext);

  // State
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState("");
  // Orchestrator UI state (transient, not persisted as messages).
  const [statusText, setStatusText] = useState("");
  const [chips, setChips] = useState(/** @type {Array<{label:string,to:string}>} */ ([]));
  const [resume, setResume] = useState(/** @type {{message:string,label:string,to:string} | null} */ (null));
  // Queue of proposed writes: the model can propose several saves in ONE turn
  // (e.g. two doelgroepen + three effecten) — every proposal keeps its own card.
  // A single-slot state made each confirmation event overwrite the previous
  // card, silently dropping all but the last proposal (inbox bug #58).
  const [confirmations, setConfirmations] = useState(/** @type {Array<{id:string,title:string,body:string,action:object}>} */ ([]));
  // Antwoordknoppen bij het laatste antwoord (idee #86) — gevoed door de
  // [[suggesties: …]]-marker die extractSuggestions uit de bubble haalt.
  const [suggestions, setSuggestions] = useState(/** @type {string[]} */ ([]));
  // Idee #85: nadat de LAATSTE openstaande kaart is bevestigd krijgt Decca
  // automatisch een vervolgbeurt (anders moest de gebruiker opnieuw prompten).
  const confirmedSinceReplyRef = useRef(false);
  // ids of cards whose action is currently executing server-side. A LIST, not
  // one slot: approving card B while card A is in-flight must not re-enable
  // A's button (that allowed a double save).
  const [confirmBusyIds, setConfirmBusyIds] = useState(/** @type {string[]} */ ([]));
  // docid of the message currently being read aloud (drives its spinner)
  const [readingAloudId, setReadingAloudId] = useState(/** @type {string | null} */ (null));
  // Surfaced backend/network failure shown as an error bubble in the chat.
  const [errorText, setErrorText] = useState("");

  // Meescrollen zolang de lezer onderaan staat (testronde 2026-10-27, B8).
  /** @type {React.MutableRefObject<HTMLDivElement | null>} */
  const containerRef = useRef(null);
  const stickToBottom = useRef(true);

  // Alles wat bij ÉÉN beurt hoort. Wordt gewist aan het begin van een nieuwe
  // beurt, én zodra de gebruiker naar een ander gesprek gaat: anders bleven de
  // antwoordknoppen van het vorige gesprek onder een vers 'Nieuw gesprek'
  // staan, alsof Decca daar zojuist iets had gevraagd.
  const resetTurnUI = useCallback(() => {
    setStatusText("");
    setChips([]);
    setResume(null);
    setConfirmations([]);
    setSuggestions([]);
    setErrorText("");
  }, []);

  useEffect(() => {
    resetTurnUI();
  }, [conversationId, resetTurnUI]);

  // Firestore (unconditional hooks)
  const stakeholders = useFirestoreOrderBy("Stakeholders", "Position", "asc") || [];
  const activities = useFirestoreOrderBy("Activities", "Position", "asc") || [];

  const messages = useFirestoreGeneralOrderBy(
    "Messages",
    "ConversationID",
    conversationId,
    "Position",
    "asc"
  );

  const firestoreCollections = {
    stakeholders: stakeholders.length,
    activities: activities.length
  };

  // Settings (call once)
  const settings = useSettings();
  const companyName = settings?.compagnyName;
  const onboarding = settings?.onboarding;
  const website = settings?.website;

  // React Router location (call once)
  const [, , page] = Location();

  // All messages -> LLM
  const allMessages = () => {
    const arr = [];
    messages &&
      messages.forEach((m) => {
        // Een opgeslagen storingsmelding is geen antwoord van Decca; het model
        // hoeft hem niet als geschiedenis te zien.
        if (m.Error === true) return;
        arr.push({
          role: m.Type,
          content: m.Message
        });
      });
    if (message.trim() !== "") {
      arr.push({ role: "user", content: message });
    }
    return arr;
  };

  // Send event to LLM with streaming. `syntheticUser` is een niet-getoond,
  // niet-opgeslagen extra user-bericht — gebruikt voor de automatische
  // vervolgbeurt na een bevestigde opslag (idee #85).
  const sendEvent = async (syntheticUser = "") => {
    const isDev = process.env.NODE_ENV === "development";
    const url = isDev
      ? "http://127.0.0.1:5001/deccos-app/europe-west1/deccaChatStream"
      : "https://europe-west1-deccos-app.cloudfunctions.net/deccaChatStream";

    // tenantOverride lets us analyze a different tenant than the logged-in client
    // (e.g. portfolio manager looking at a viewed project's tenant)
    const effectiveTenantId = tenantOverride || client;
    const effectiveTenantName = tenantOverride ? (tenantNameOverride || '') : companyName;

    const body = {
      data: {
        subject,
        page,
        contextData,
        user: { name: auth?.ForName || "" },
        company: {
          name: effectiveTenantName,
          id: effectiveTenantId,
          onboarding: tenantOverride ? null : onboarding,
          website: tenantOverride ? null : website,
        },
        messages: syntheticUser
          ? [...allMessages(), { role: "user", content: syntheticUser }]
          : allMessages(),
        selectedActivity,
        selectedEffect,
        // tocContext tells the backend which TOC step/node/mode the user is on
        // (null when Decca is opened outside the TOC navigator). The orchestrator
        // ignores it when absent — behaviour is unchanged off the TOC page.
        tocContext: tocContext || null,
        // entryPoint ('topbar' | 'button') is a routing hint, not a lock.
        entryPoint: entryPoint || "button",
        // Opt into the orchestrator path. The backend tenant gate requires an
        // Authorization header whenever this is true.
        ...(USE_ORCHESTRATOR ? { orchestrator: true } : {}),
      },
    };

    // Reset transient orchestrator UI from the previous turn.
    resetTurnUI();
    // Elke (nieuwe) beurt consumeert de vervolg-trigger — voorkomt een dubbele
    // automatische beurt wanneer de gebruiker zelf alweer getypt heeft.
    confirmedSinceReplyRef.current = false;

    // Both chat paths are tenant-gated server-side — always send the Firebase
    // ID token so the gate works even after a flag-off rollback to the legacy path.
    const headers = { "Content-Type": "application/json" };
    const idToken = await firebaseAuth.currentUser?.getIdToken();
    if (idToken) headers.Authorization = `Bearer ${idToken}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      // A failure before the stream opens (e.g. tenant gate, 500) won't arrive as
      // an SSE event — surface its body so the chat screen shows what went wrong.
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        setStatusText("");
        setErrorText(text || `Decca gaf een fout (${response.status}).`);
        return;
      }

      let streamedContent = "";

      // Blocks can straddle network chunks (a large confirmation card does);
      // the shared reader buffers them and awaits each handler in order.
      // AI-CONTEXT: See docs/pitfalls.md#api-sse-event-split-across-chunks
      await parseSseStream(response.body, async (event) => {
        if (event.type === "chunk") {
          // First token of the final answer — clear the "working…" status.
          setStatusText("");
          streamedContent += event.content;
          // De suggestie-marker ([[suggesties: …]]) mag ook tijdens het
          // streamen nooit als tekst zichtbaar worden.
          setStreamingMessage(extractSuggestions(streamedContent).text);
        } else if (event.type === "status") {
          // Transient progress line (multi-step work). Not saved as a message.
          setStatusText(event.content);
        } else if (event.type === "chip") {
          setChips((prev) => [...prev, { label: event.label, to: event.to }]);
        } else if (event.type === "resume") {
          setResume({ message: event.message, label: event.label, to: event.to });
        } else if (event.type === "confirmation") {
          // Proposed write — append a card; nothing is saved until approved.
          setStatusText("");
          setConfirmations((prev) => [
            ...prev,
            { id: uuid(), title: event.title, body: event.body, action: event.action },
          ]);
        } else if (event.type === "tool") {
          // Tool call result — save as a single complete message, no streaming
          setStreamingMessage("");
          await saveMessageToFirestore(event.content, "decca", 2);
          streamedContent = "";
        } else if (event.type === "error") {
          // Orchestrator surfaced a failure mid-stream — show it in the chat
          // and don't leave the UI stuck on "Decca denkt na…".
          // Opgeslagen in het gesprek (Error: true), zodat de vraag niet zonder
          // antwoord achterblijft zodra de volgende beurt de beurt-UI wist
          // (testronde 2026-10-27, B3).
          console.error("Decca orchestrator error:", event.content);
          setStatusText("");
          setStreamingMessage("");
          streamedContent = "";
          await saveMessageToFirestore(event.content || "Er ging iets mis bij Decca.", "decca", 2, { Error: true });
        } else if (event.type === "done") {
          setStatusText("");
          if (streamedContent) {
            setStreamingMessage("");
            // Marker eruit: de tekst wordt opgeslagen, de opties worden
            // antwoordknoppen bij dit antwoord (idee #86).
            const { text, suggestions: options } = extractSuggestions(streamedContent);
            await saveMessageToFirestore(text, "decca", 2);
            setSuggestions(options);
          }
        }
      });
    } catch (error) {
      console.error("Error sending event:", error);
      setStatusText("");
      setStreamingMessage("");
      setErrorText("Kon Decca niet bereiken. Probeer het opnieuw.");
    }
  };

  // Confirmation card: user approved a proposed write → execute it server-side.
  /** @param {{id:string,title:string,body:string,action:object}} card */
  const approveAction = async (card) => {
    if (!card || confirmBusyIds.includes(card.id)) return;
    setConfirmBusyIds((prev) => [...prev, card.id]);

    const isDev = process.env.NODE_ENV === "development";
    const url = isDev
      ? "http://127.0.0.1:5001/deccos-app/europe-west1/deccaChatStream"
      : "https://europe-west1-deccos-app.cloudfunctions.net/deccaChatStream";

    const effectiveTenantId = tenantOverride || client;
    const effectiveTenantName = tenantOverride ? (tenantNameOverride || "") : companyName;

    const headers = { "Content-Type": "application/json" };
    const idToken = await firebaseAuth.currentUser?.getIdToken();
    if (idToken) headers.Authorization = `Bearer ${idToken}`;

    const body = {
      data: {
        user: { name: auth?.ForName || "" },
        company: { name: effectiveTenantName, id: effectiveTenantId },
        orchestrator: true,
        approvedAction: card.action,
      },
    };

    // A failed execute must KEEP the card — the error text says "probeer het
    // opnieuw", so there has to be something left to retry with.
    let succeeded = true;
    try {
      const response = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });
      await parseSseStream(response.body, async (event) => {
        if (event.type === "tool") {
          await saveMessageToFirestore(event.content, "decca", 2);
        } else if (event.type === "error") {
          console.error("Decca confirm error:", event.content);
          setErrorText(event.content || "Opslaan mislukt.");
          succeeded = false;
        }
      });
    } catch (error) {
      console.error("Error approving action:", error);
      setErrorText("Kon de actie niet uitvoeren. Probeer het opnieuw.");
      succeeded = false;
    }

    setConfirmBusyIds((prev) => prev.filter((id) => id !== card.id));
    // Only a successfully executed card leaves the queue — other proposals
    // stay approvable, and a failed one stays available for retry.
    if (succeeded) {
      confirmedSinceReplyRef.current = true;
      setConfirmations((prev) => prev.filter((c) => c.id !== card.id));
    }
  };

  /** @param {{id:string}} card */
  const cancelAction = (card) =>
    setConfirmations((prev) => prev.filter((c) => c.id !== card.id));

  // Idee #85: zodra de wachtrij leegloopt na ≥1 succesvolle bevestiging krijgt
  // Decca automatisch een vervolgbeurt — hij benoemt wat is opgeslagen en
  // stelt zelf de volgende stap voor (de gebruiker hoefde eerst opnieuw te
  // prompten). Het synthetische bericht wordt niet getoond of opgeslagen.
  useEffect(() => {
    if (confirmations.length > 0 || !confirmedSinceReplyRef.current || loading) return;
    confirmedSinceReplyRef.current = false;
    const followUp = async () => {
      setLoading(true);
      await sendEvent(
        "De gebruiker heeft de openstaande voorstellen zojuist bevestigd; alles is opgeslagen. " +
        "Benoem in één zin wat er is bijgewerkt en stel direct de volgende logische stap voor."
      );
      setLoading(false);
    };
    followUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmations]);

  // Meescrollen bij alles wat onderaan bijkomt: een bericht, de laadmelding, de
  // statusregel, het stromende antwoord, kaarten en knoppen. Alleen als de lezer
  // onderaan stond (B8). Primitieven als deps, geen afgeleide arrays.
  const messageCount = messages?.length || 0;
  useEffect(() => {
    const container = containerRef.current;
    if (container && stickToBottom.current) container.scrollTop = container.scrollHeight;
  }, [messageCount, loading, statusText, streamingMessage, errorText, confirmations.length, suggestions.length, chips.length]);

  /**
   * Verstuur een tekst als user-bericht die niet uit het invoerveld komt
   * (een antwoordknop, of de eerste vraag uit het lege scherm).
   * @param {string} text
   */
  const sendUserText = async (text) => {
    stickToBottom.current = true;
    setLoading(true);
    await saveMessageToFirestore(text, "user", 1);
    await sendEvent(text);
    setLoading(false);
  };

  // Antwoordknop (idee #86): verstuur de optie als gewoon user-bericht.
  /** @param {string} option */
  const submitSuggestion = async (option) => {
    if (loading || !option) return;
    setSuggestions([]);
    await sendUserText(option);
  };

  // Eerste vraag uit het lege scherm (testronde 2026-10-27, B9). Pas versturen
  // als het welkomstbericht er is: anders krijgen ze allebei Position 1 en is
  // de volgorde willekeurig.
  // AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
  const initialSentRef = useRef(false);
  useEffect(() => {
    if (!initialMessage || initialSentRef.current || messageCount < 1 || loading) return;
    initialSentRef.current = true;
    sendUserText(initialMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, messageCount, loading]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      commitMessage();
    }
  };

  const commitMessage = async () => {
    if (message.trim() === "") return;

    // Wie zelf een vraag stelt, wil het antwoord zien.
    stickToBottom.current = true;
    setLoading(true);

    // Save user message
    await saveMessageToFirestore(message, "user", 1);

    // Clear input after saving
    setMessage("");

    // Send to LLM
    await sendEvent();

    setLoading(false);
  };

  // Save message to correct collection
  /**
   * @param {string} text
   * @param {string} type - 'user' of 'decca'
   * @param {number} position - Offset op het aantal berichten
   * @param {{Error?: boolean}} [extra] - `Error: true` = storingsmelding (geen antwoord)
   */
  const saveMessageToFirestore = async (text, type, position, extra = {}) => {
    const base = {
      ID: uuid(),
      Compagny: client,
      CompagnyID: client,
      Position: (messages?.length || 0) + position,
      Timestamp: timestamp,
      Message: text,
      Type: type,
      ConversationID: conversationId,
      ...extra
    };

    const payload = { ...base, UserID: auth?.ID || null, UserName: auth?.ForName || null };

    try {
      await db.collection("Messages").doc().set(payload);
    } catch (error) {
      console.error("Error saving message to Firestore:", error);
    }
  };

  // Delete message from correct collection
  const deleteMessageHandler = async (docId) => {
    try {
      await db.collection("Messages").doc(docId).delete();
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  // Hover-revealed read-aloud + delete actions for a single message.
  // Plain helper (invoked, not mounted) — avoids the component-in-render pitfall.
  const renderMessageActions = (m, end = false) => (
    <div className={`dz-msg-actions dz-reveal${end ? " dz-msg-actions-end" : ""}`}>
      <Tooltip content="Bericht voorlezen" left="-100px" top="-50px" width="25px">
        <button
          type="button"
          className="dz-ib dz-ib-sm"
          aria-label="Bericht voorlezen"
          data-question={htmlToText(m.Message)}
          disabled={readingAloudId === m.docid}
          onClick={(e) =>
            readAloud(e, (loading) => setReadingAloudId(loading ? m.docid : null))
          }
        >
          {readingAloudId === m.docid ? (
            <span className="dz-spinner" aria-label="Laden" />
          ) : (
            <DeccaIcon name="speaker" size={14} />
          )}
        </button>
      </Tooltip>
      <Tooltip content="Bericht verwijderen" left="-100px" top="-50px" width="25px">
          <button
            type="button"
            className="dz-ib dz-ib-sm dz-ib-danger"
            aria-label="Bericht verwijderen"
            onClick={() => deleteMessageHandler(m.docid)}
          >
            <DeccaIcon name="trash" size={14} />
          </button>
        </Tooltip>
    </div>
  );

  // AI-CONTEXT: See docs/pitfalls.md#ui-redesign-class-render-css-drift
  // dz-view-chat fills the column; classNames here must match decca.css selectors.
  return (
    <div className="dz-view-chat">
      <div
        id="decca-conversation-container"
        ref={containerRef}
        onScroll={() => { stickToBottom.current = isNearBottom(containerRef.current); }}
      >
        {messages &&
          messages.map((m, index) => {
            if (m.Type === "user") {
              return (
                <div key={index} className="dz-msg dz-msg-user dz-rise">
                  <div className="dz-bubble-user">
                    <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.Message) }}></p>
                  </div>
                  {renderMessageActions(m, true)}
                </div>
              );
            }

            // Decca side: avatar + name only on the first of a consecutive run
            const firstDecca = index === 0 || messages[index - 1]?.Type !== m.Type;
            return (
              <div key={index} className="dz-msg dz-rise">
                <div className="dz-msg-avatar">
                  {firstDecca && <DeccaAvatar size="sm" />}
                </div>
                <div className="dz-msg-body">
                  {firstDecca && (
                    <div className="dz-msg-name">
                      <span className="dz-msg-name-main">Decca</span>
                      <span className="dz-msg-name-sub">Impact-adviseur</span>
                    </div>
                  )}
                  <div
                    className={m.Error === true ? "dz-bubble-decca dz-bubble-error" : "dz-bubble-decca"}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.Message, { allowedTags: DECCA_ALLOWED_TAGS }) }}
                  ></div>
                  {renderMessageActions(m)}
                </div>
              </div>
            );
          })}

        {loading && !streamingMessage && (
          <div className="dz-msg dz-rise">
            <div className="dz-msg-avatar">
              <DeccaAvatar size="sm" />
            </div>
            <div className="dz-msg-body">
              <div className="dz-typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        )}

        {streamingMessage && (
          <div className="dz-msg dz-rise">
            <div className="dz-msg-avatar">
              <DeccaAvatar size="sm" />
            </div>
            <div className="dz-msg-body">
              <div
                className="dz-bubble-decca"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(streamingMessage, { allowedTags: DECCA_ALLOWED_TAGS }) }}
              ></div>
            </div>
          </div>
        )}

        {/* Orchestrator: transient progress line during multi-step work */}
        <StatusLine text={statusText} />

        {/* Backend/network failure surfaced to the user */}
        {errorText && (
          <div className="dz-msg dz-rise" role="alert">
            <div className="dz-msg-avatar">
              <DeccaAvatar size="sm" />
            </div>
            <div className="dz-msg-body">
              <div className="dz-bubble-decca dz-bubble-error">{errorText}</div>
            </div>
          </div>
        )}

        {/* Orchestrator: deep-link chips attached to the latest answer */}
        {chips.length > 0 && (
          <div className="decca-chips-row">
            {chips.map((chip, i) => (
              <DeepLinkChip key={i} label={chip.label} to={chip.to} />
            ))}
          </div>
        )}

        {/* Antwoordknoppen bij het laatste antwoord (idee #86) */}
        {suggestions.length > 0 && !loading && (
          <div className="decca-suggesties-row">
            {suggestions.map((option) => (
              <button
                key={option}
                type="button"
                className="decca-suggestie-btn"
                onClick={() => submitSuggestion(option)}
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {/* Orchestrator: resume cue for an unfinished guided flow */}
        {resume && (
          <ResumeChip message={resume.message} label={resume.label} to={resume.to} />
        )}

        {/* Orchestrator: one confirmation card per proposed write */}
        {confirmations.map((card) => (
          <ConfirmationCard
            key={card.id}
            title={card.title}
            body={card.body}
            onApprove={() => approveAction(card)}
            onCancel={() => cancelAction(card)}
            busy={confirmBusyIds.includes(card.id)}
          />
        ))}
      </div>

      <InputContainer
        setMessage={setMessage}
        message={message}
        loading={loading}
        handleKeyDown={handleKeyDown}
        commitMessage={commitMessage}
      />
    </div>
  );
};

export default Decca;
